import {
    SET_SEARCH_QUERY,
    SET_ASSIGNEE,
    SET_AUTHOR,
    SET_SELECTED_TAGS,
    TOGGLE_TAG,
    SET_TAG_METHOD,
    SET_BOOKMARKED_ONLY,
    SET_ANCESTOR_ID,
    SET_STATUS_FILTER,
    SET_CHANNEL_IDS,
    TOGGLE_CHANNEL_ID,
    TOGGLE_GROUP_CHANNELS,
    CLEAR_CHANNEL_IDS,
    SET_MEDIA_TYPE_FILTER,
    SET_SENT_BY_FILTER,
    SET_SENT_TO_FILTER,
    SET_DATE_RANGE_FILTER,
    CLEAR_ALL_FILTERS,
    SET_APPLIED_FILTERS,
    SET_SEARCH_RESULTS,
    UPDATE_FOLDER,
    DELETE_FOLDER,
    UPDATE_LINK,
    DELETE_LINK,
    SET_ANCESTOR_INFO,
    SET_USERS,
    SET_TAGS,
    SET_RECENT_TAGS,
    SET_SEARCH_LOADING,
    SET_FETCHING_NEW_DATA,
    SET_VIEW_MODE,
    SET_ACTIVE_TAB,
    SET_PAGE_SIZE,
    SET_PAGE,
    SET_FOLDERS_PAGE,
    SET_LINKS_PAGE,
    SET_MESSAGES_PAGE,
    SET_INITIALIZED
} from '../constants/search.constant';

// Helper: dedupe + stable numeric sort
const normalizeIds = (ids) => {
    return Array.from(new Set((ids || []).map(Number).filter(Number.isFinite))).sort((a, b) => a - b);
};

const initialFilters = {
    query: '',
    assignee: null,
    author: null,
    selectedTags: [],
    tagMethodOr: true,
    bookmarkedOnly: false,
    ancestorId: null,
    status: [],
    channelIds: [],
    mediaType: null,
    sentBy: null,
    sentTo: null,
    dateFrom: null,
    dateTo: null
};

const initialState = {
    // Filters (current values in form)
    filters: { ...initialFilters },

    // Applied filters (what was actually searched)
    appliedFilters: { ...initialFilters },

    // Results
    results: {
        folders: [],
        links: [],
        messages: [],
        tasks: []
    },
    ancestorInfo: null,

    // Reference data (shared/cached)
    users: [],
    tags: [],
    recentTags: [],

    // Loading states
    isLoading: false,
    isFetchingNewData: false,

    // UI state
    viewMode: 'split', // 'tabs' | 'split'
    activeTab: 0,      // 0 for folders, 1 for links, 2 for messages
    pageSize: 25,
    page: 1,           // for tabs mode
    foldersPage: 1,    // for split mode
    linksPage: 1,      // for split mode
    messagesPage: 1,   // for split mode

    // Initialization flag
    isInitialized: false
};

const searchReducer = (state = initialState, action) => {
    switch (action.type) {
        // Filter actions
        case SET_SEARCH_QUERY:
            return {
                ...state,
                filters: { ...state.filters, query: action.payload }
            };

        case SET_ASSIGNEE:
            return {
                ...state,
                filters: { ...state.filters, assignee: action.payload }
            };

        case SET_AUTHOR:
            return {
                ...state,
                filters: { ...state.filters, author: action.payload }
            };

        case SET_SELECTED_TAGS:
            return {
                ...state,
                filters: { ...state.filters, selectedTags: action.payload }
            };

        case TOGGLE_TAG:
            const tagId = action.payload;
            const currentTags = state.filters.selectedTags;
            const newTags = currentTags.includes(tagId)
                ? currentTags.filter(id => id !== tagId)
                : [...currentTags, tagId];
            return {
                ...state,
                filters: { ...state.filters, selectedTags: newTags }
            };

        case SET_TAG_METHOD:
            return {
                ...state,
                filters: { ...state.filters, tagMethodOr: action.payload }
            };

        case SET_BOOKMARKED_ONLY:
            return {
                ...state,
                filters: { ...state.filters, bookmarkedOnly: action.payload }
            };

        case SET_ANCESTOR_ID:
            return {
                ...state,
                filters: { ...state.filters, ancestorId: action.payload }
            };

        case SET_STATUS_FILTER:
            return {
                ...state,
                filters: { ...state.filters, status: action.payload }
            };

        case SET_CHANNEL_IDS:
            return {
                ...state,
                filters: { ...state.filters, channelIds: normalizeIds(action.payload) }
            };

        case TOGGLE_CHANNEL_ID: {
            const id = Number(action.payload);
            if (!Number.isFinite(id)) return state;

            const set = new Set(state.filters.channelIds || []);
            if (set.has(id)) set.delete(id);
            else set.add(id);

            return {
                ...state,
                filters: { ...state.filters, channelIds: Array.from(set).sort((a, b) => a - b) }
            };
        }

        case TOGGLE_GROUP_CHANNELS: {
            const { channelIds } = action.payload || {};
            const groupIds = normalizeIds(channelIds);

            const selected = new Set(state.filters.channelIds || []);
            const anyMissing = groupIds.some((id) => !selected.has(id));

            if (anyMissing) {
                // add all
                groupIds.forEach((id) => selected.add(id));
            } else {
                // remove all
                groupIds.forEach((id) => selected.delete(id));
            }

            return {
                ...state,
                filters: { ...state.filters, channelIds: Array.from(selected).sort((a, b) => a - b) }
            };
        }

        case CLEAR_CHANNEL_IDS:
            return {
                ...state,
                filters: { ...state.filters, channelIds: [] }
            };

        case SET_MEDIA_TYPE_FILTER:
            return {
                ...state,
                filters: { ...state.filters, mediaType: action.payload }
            };

        case SET_SENT_BY_FILTER:
            return {
                ...state,
                filters: { ...state.filters, sentBy: action.payload }
            };

        case SET_SENT_TO_FILTER:
            return {
                ...state,
                filters: { ...state.filters, sentTo: action.payload }
            };

        case SET_DATE_RANGE_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    dateFrom: action.payload.from,
                    dateTo: action.payload.to
                }
            };

        case CLEAR_ALL_FILTERS:
            return {
                ...state,
                filters: { ...initialFilters },
                appliedFilters: { ...initialFilters },
                results: { folders: [], links: [], messages: [], tasks: [] },
                ancestorInfo: null
            };

        case SET_APPLIED_FILTERS:
            return {
                ...state,
                appliedFilters: action.payload
            };

        // Results actions
        case SET_SEARCH_RESULTS:
            return {
                ...state,
                results: {
                    folders: action.payload.folders || [],
                    links: action.payload.links || [],
                    messages: action.payload.messages || [],
                    tasks: action.payload.tasks || []
                },
                // Reset pagination to page 1 when new results come in
                page: 1,
                foldersPage: 1,
                linksPage: 1,
                messagesPage: 1
            };

        case UPDATE_FOLDER:
            const { id, updates } = action.payload;
            let updatedFolders = state.results.folders.map(folder =>
                folder.id === id ? { ...folder, ...updates } : folder
            );
            // Re-sort if star_level changed
            if (updates.star_level !== undefined) {
                updatedFolders.sort((a, b) => {
                    const aArchived = a.is_archived || false;
                    const bArchived = b.is_archived || false;
                    if (aArchived !== bArchived) return aArchived ? 1 : -1;
                    return b.star_level - a.star_level;
                });
            }
            return {
                ...state,
                results: { ...state.results, folders: updatedFolders }
            };

        case DELETE_FOLDER:
            return {
                ...state,
                results: {
                    ...state.results,
                    folders: state.results.folders.filter(f => f.id !== action.payload)
                }
            };

        case UPDATE_LINK:
            const { id: linkId, updates: linkUpdates } = action.payload;
            let updatedLinks = state.results.links.map(link =>
                link.id === linkId ? { ...link, ...linkUpdates } : link
            );
            // Re-sort if star_level changed
            if (linkUpdates.star_level !== undefined) {
                updatedLinks.sort((a, b) => {
                    const aArchived = a.is_archived || false;
                    const bArchived = b.is_archived || false;
                    if (aArchived !== bArchived) return aArchived ? 1 : -1;
                    return b.star_level - a.star_level;
                });
            }
            return {
                ...state,
                results: { ...state.results, links: updatedLinks }
            };

        case DELETE_LINK:
            return {
                ...state,
                results: {
                    ...state.results,
                    links: state.results.links.filter(l => l.id !== action.payload)
                }
            };

        case SET_ANCESTOR_INFO:
            return {
                ...state,
                ancestorInfo: action.payload
            };

        // Reference data actions
        case SET_USERS:
            return { ...state, users: action.payload };

        case SET_TAGS:
            return { ...state, tags: action.payload };

        case SET_RECENT_TAGS:
            return { ...state, recentTags: action.payload };

        // Loading states
        case SET_SEARCH_LOADING:
            return { ...state, isLoading: action.payload };

        case SET_FETCHING_NEW_DATA:
            return { ...state, isFetchingNewData: action.payload };

        // UI state actions
        case SET_VIEW_MODE:
            return { ...state, viewMode: action.payload };

        case SET_ACTIVE_TAB:
            return { ...state, activeTab: action.payload };

        case SET_PAGE_SIZE:
            return {
                ...state,
                pageSize: action.payload,
                page: 1,
                foldersPage: 1,
                linksPage: 1,
                messagesPage: 1
            };

        case SET_PAGE:
            return { ...state, page: action.payload };

        case SET_FOLDERS_PAGE:
            return { ...state, foldersPage: action.payload };

        case SET_LINKS_PAGE:
            return { ...state, linksPage: action.payload };

        case SET_MESSAGES_PAGE:
            return { ...state, messagesPage: action.payload };

        // Initialization
        case SET_INITIALIZED:
            return { ...state, isInitialized: action.payload };

        default:
            return state;
    }
};

export default searchReducer;

